import React, { useState } from 'react';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import { useParams, useHistory, Link } from 'react-router-dom';
import { Content } from '../../components/light/Content';
import { SectionBlock } from '../../components/light/SectionBlock';
import { useQueryString } from '../../libs/QueryString';
import { DateTime } from '../../libs/DateTime';
import { Loading } from '../../components/Loading';
import downloadExcelFile from '../../libs/downloadExcelFile';

const QUERY = gql`
  query CustomerDepositStatement($customerId: Int!, $asAt: String!) {
    customerDepositStatement(customerId: $customerId, asAt: $asAt) {
      datetime
      description
      coid
      orderID
      refundID
      returnID
      creditHistoryID
      amount
      balance
    }
  }
`;

const CUSTOMER_LIST_QUERY = gql`
  query customerList($offset: Int!, $limit: Int!, $phone: String, $active: Boolean) {
    customerList(offset: $offset, limit: $limit, phoneNumber: $phone, active: $active) {
      id
      name
      phoneNumber
      balance
      loan
      idCard
      gender
      spend
      active
      blockedInfo {
        blockedDate
        message
        __typename
      }
      __typename
    }
  }
`;

interface StatementItem {
  datetime: string;
  description: string;
  coid: number | null;
  orderID: number | null;
  refundID: number | null;
  returnID: number | null;
  creditHistoryID: number | null;
  amount: number;
  balance: number;
}

interface QueryResult {
  customerDepositStatement: StatementItem[];
}

export function CustomerDepositStatementScreen(): React.ReactElement {
  const { customerId } = useParams<{ customerId: string }>();
  const history = useHistory();
  const [searchPhone, setSearchPhone] = useState('');

  const { asAt } = useQueryString<{ asAt: string }>({
    asAt: DateTime.today(),
  });

  const { loading, data, error } = useQuery<QueryResult>(QUERY, {
    variables: {
      customerId: customerId ? parseInt(customerId, 10) : 0,
      asAt,
    },
    skip: !customerId,
  });

  const [fetchCustomer, { loading: searchLoading, error: searchError }] = useLazyQuery(CUSTOMER_LIST_QUERY, {
    onCompleted: searchData => {
      if (searchData?.customerList?.length > 0) {
        const id = searchData.customerList[0].id;
        history.push(`/audit/customer-deposit-statement/${id}?asAt=${asAt}`);
      } else {
        alert('Customer not found');
      }
    },
  });

  const handleSearchCustomer = () => {
    if (searchPhone.trim() === '') return;
    fetchCustomer({
      variables: {
        offset: 0,
        limit: 1,
        phone: searchPhone.trim(),
        active: null,
      },
    });
  };

  const handleExportExcel = () => {
    if (!data) return;
    const rows = [
      [
        'Date Time',
        'Description',
        'COID',
        'Order ID',
        'Refund ID',
        'Return ID',
        'Credit History ID',
        'Amount',
        'Balance',
      ],
      ...data.customerDepositStatement.map(item => [
        item.datetime,
        item.description,
        item.coid?.toString() || '-',
        item.orderID?.toString() || '-',
        item.refundID?.toString() || '-',
        item.returnID?.toString() || '-',
        item.creditHistoryID?.toString() || '-',
        item.amount.toString(),
        item.balance.toString(),
      ]),
    ];
    downloadExcelFile(`Customer_Deposit_Statement_${customerId || 'Unknown'}.xlsx`, 'Statement', rows);
  };

  return (
    <Content>
      <SectionBlock
        title={
          <span>
            Customer Deposit Statement
            {customerId && (
              <>
                {' '}
                Customer ID:{' '}
                <Link to={`/customers/edit/${customerId}`} target="_blank">
                  {customerId}
                </Link>
              </>
            )}
          </span>
        }
      >
        <div className="controls-above-table">
          <form action="" method="GET">
            <div className="mb-1 d-flex justify-content-between align-items-center">
              <div>
                <div style={{ display: 'inline-block', width: 60 }}>As At</div>
                <input type="date" name="asAt" className="lf-form-control lf-inline" defaultValue={asAt} />
                <button className="lf-btn btn-primary ml-1">Filter</button>
              </div>
              <div>
                <div style={{ display: 'inline-block', width: 150 }}>Search Phone:</div>
                <input
                  type="text"
                  className="lf-form-control lf-inline"
                  placeholder="Phone number"
                  value={searchPhone}
                  onChange={e => setSearchPhone(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchCustomer();
                    }
                  }}
                />
                <button
                  type="button"
                  className="lf-btn btn-primary ml-1"
                  onClick={handleSearchCustomer}
                  disabled={searchLoading}
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
            <div className="mb-1 d-flex justify-content-end mt-2">
              {data && (
                <button type="button" className="lf-btn btn-success" onClick={handleExportExcel}>
                  Export to Excel
                </button>
              )}
            </div>
          </form>
        </div>

        {loading && <Loading />}
        {error && <div className="alert alert-danger">{error.message}</div>}

        {data && (
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Date Time</th>
                <th>Description</th>
                <th>COID</th>
                <th>Order ID</th>
                <th>Refund ID</th>
                <th>Return ID</th>
                <th>Credit History ID</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.customerDepositStatement.map((item, index) => (
                <tr key={index}>
                  <td>{item.datetime}</td>
                  <td>{item.description}</td>
                  <td>{item.coid || '-'}</td>
                  <td>
                    {item.orderID ? (
                      <Link to={`/orders/detail/${item.orderID}`} target="_blank">
                        {item.orderID}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {item.refundID ? (
                      <Link to={`/customers/refund/detail/${item.refundID}`} target="_blank">
                        {item.refundID}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {item.returnID ? (
                      <Link to={`/customers/order/return?search=RI${item.returnID}`} target="_blank">
                        {item.returnID}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {item.creditHistoryID ? (
                      <Link to={`/payments/detail/${item.creditHistoryID}`} target="_blank">
                        {item.creditHistoryID}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="text-right">{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="text-right">{item.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
              {data.customerDepositStatement.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </SectionBlock>
    </Content>
  );
}
